// app/javascript/controllers/thank_you_editor_controller.js
import { Controller } from "@hotwired/stimulus";
import Swal from "sweetalert2";

export default class extends Controller {
    static targets = [
        "form", "submit", "headline", "body", "impact", "noun", "tone",
        "previewHeadline", "previewBody"
    ];
    static values = { suggestUrl: String };

    connect() {
        this.sample = {
            parent_first_name: "Alex",
            guest_first_name:  "Sam",
            hosted_for_name:   "Maya",
            party_title:       "Maya’s 7th Birthday Bash",
            charity_name:      this._charityNameFromPage(),
        };
        // Listen for input to refresh preview
        this.formTarget.addEventListener("input", () => this.renderPreview());
        this.renderPreview();
    }

    confirmAndSubmit(event) {
        event.preventDefault();
        Swal.fire({
            title: "Save changes?",
            text: "Your tailored message will be used for all thank-you notes for this charity.",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Save",
            cancelButtonText: "Cancel",
        }).then((r) => {
            if (r.isConfirmed) this.formTarget.submit();
        });
    }

    async suggest() {
        if (!this.hasSuggestUrlValue) return;
        try {
            Swal.fire({ title: "Drafting copy…", didOpen: () => Swal.showLoading(), allowOutsideClick: false, allowEscapeKey: false });
            const res = await fetch(this.suggestUrlValue, {
                method: "POST",
                headers: {
                    "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
                    "Accept": "application/json"
                },
                credentials: "same-origin"
            });
            const json = await res.json();
            Swal.close();

            if (!res.ok || json.error) {
                Swal.fire("Sorry", json.error || "Suggestion unavailable right now.", "error");
                return;
            }

            const { impact_sentence, body_template, beneficiary_noun, headline_template } = json;

            const html = `
        <div class="text-start">
          <p class="mb-1"><strong>Headline</strong></p>
          <pre class="p-2 bg-light rounded" style="white-space:pre-wrap">${this._escape(headline_template || "")}</pre>
          <p class="mb-1 mt-3"><strong>Impact sentence</strong></p>
          <pre class="p-2 bg-light rounded" style="white-space:pre-wrap">${this._escape(impact_sentence || "")}</pre>
          <p class="mb-1 mt-3"><strong>Beneficiary noun</strong></p>
          <pre class="p-2 bg-light rounded" style="white-space:pre-wrap">${this._escape(beneficiary_noun || "")}</pre>
          <p class="mb-1 mt-3"><strong>Body</strong></p>
          <pre class="p-2 bg-light rounded" style="white-space:pre-wrap">${this._escape(body_template || "")}</pre>
        </div>
      `;

            const choice = await Swal.fire({
                title: "Use this draft?",
                html,
                icon: "info",
                showCancelButton: true,
                confirmButtonText: "Apply",
                cancelButtonText: "Cancel",
                width: 800
            });

            if (choice.isConfirmed) {
                if (headline_template) this.headlineTarget.value = headline_template;
                if (impact_sentence)  this.impactTarget.value   = impact_sentence;
                if (beneficiary_noun) this.nounTarget.value = beneficiary_noun;
                if (body_template)    this.bodyTarget.value     = body_template;
                this.renderPreview();
            }
        } catch (e) {
            Swal.close();
            Swal.fire("Error", "Network error while drafting.", "error");
            console.error(e);
        }
    }

    renderPreview() {
        const ctx = {
            ...this.sample,
            impact_sentence:   this.impactTarget.value || "",
            beneficiary_noun:  this.nounTarget.value   || "",
            // (tone currently not interpolated; used just for GPT prompt)
        };

        this.previewHeadlineTarget.textContent = this._interpolate(this.headlineTarget.value || "", ctx);
        this.previewBodyTarget.textContent     = this._interpolate(this.bodyTarget.value     || "", ctx);
    }

    // --- helpers ---
    _interpolate(template, ctx) {
        return (template || "").replace(/\{\{\s*([a-z_]+)\s*\}\}/g, (_, key) => {
            return (ctx[key] ?? "").toString();
        });
    }

    _escape(s) {
        const div = document.createElement("div");
        div.innerText = s;
        return div.innerHTML;
    }

    _charityNameFromPage() {
        const el = document.querySelector(".h4,.h3,.h2");
        return el?.textContent?.replace(/^Edit Thank-You Copy — /, "") || "Your Charity";
    }
}